import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '@core/config/api.config';
import { ApiEndpoints } from '@core/config/api-endpoints';
import {
  PaginatedResult,
  PaginationParams,
  createPaginatedResult,
} from '@core/common/models/pagination.models';
import {
  AttributeDefinitionMutationResult,
  ProductAttributeDefinition,
} from '@features/catalog/models/product-attribute-definition.models';
import {
  GraphqlMutationAttributeDefinitionPayloadDto,
  GraphqlProductAttributeDefinitionByIdDataDto,
} from './dto/product-attribute-definition.dto';
import {
  asDefinitionDtoArray,
  mapMutationDefinitionPayload,
  mapProductAttributeDefinitionDto,
} from './mappers/product-attribute-definition.mapper';
import { ensureNoTopLevelErrors, GraphqlHttpResponse, mapEntityMutationResult, mapLifecycleMutationResult } from './mappers/graphql-error.mapper';

const LIST_QUERY = `
  query GetProductAttributeDefinitions($activeOnly: Boolean = true, $name: String, $limit: Int = 50, $offset: Int = 0) {
    productAttributeDefinitions(activeOnly: $activeOnly, name: $name, limit: $limit, offset: $offset) {
      items {
        id
        companyId
        name
        slug
        position
        active
        productsCount
        productAttributeValues(activeOnly: true) {
          id
          value
          position
          active
        }
      }
      total
      limit
      offset
    }
  }
`;

const BY_ID_QUERY = `
  query GetProductAttributeDefinitionById($id: ID!) {
    productAttributeDefinition(id: $id) {
      id
      name
      slug
      position
      active
      productAttributeValues(activeOnly: true) {
        id
        value
        position
        active
      }
    }
  }
`;

const BY_NAME_QUERY = `
  query GetProductAttributeDefinitionByName($name: String!) {
    productAttributeDefinition(name: $name) {
      id
      name
      slug
      position
      active
    }
  }
`;

const CREATE_MUTATION = `
  mutation CreateProductAttributeDefinition($name: String!, $slug: String, $position: Int, $active: Boolean) {
    createProductAttributeDefinition(name: $name, slug: $slug, position: $position, active: $active) {
      definition {
        id
        name
        slug
        position
        active
      }
      errors
    }
  }
`;

const UPDATE_MUTATION = `
  mutation UpdateProductAttributeDefinition($id: ID!, $name: String, $slug: String, $position: Int, $active: Boolean) {
    updateProductAttributeDefinition(id: $id, name: $name, slug: $slug, position: $position, active: $active) {
      definition {
        id
        name
        slug
        position
        active
      }
      errors
    }
  }
`;

const DESTROY_MUTATION = `
  mutation DestroyProductAttributeDefinition($id: ID!) {
    destroyProductAttributeDefinition(id: $id) {
      definition {
        id
        name
        active
      }
      errors
    }
  }
`;

const PERMANENTLY_DELETE_MUTATION = `
  mutation PermanentlyDeleteProductAttributeDefinition($id: ID!) {
    permanentlyDeleteProductAttributeDefinition(id: $id) {
      success
      errors
    }
  }
`;

@Injectable()
export class AttributeDefinitionApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly baseUrl: string
  ) {}

  private graphqlUrl(): string {
    return `${this.baseUrl}${ApiEndpoints.graphql}`;
  }

  list(
    filters?: { activeOnly?: boolean | null; name?: string | null },
    pagination?: PaginationParams
  ): Observable<PaginatedResult<ProductAttributeDefinition>> {
    const activeOnly = filters?.activeOnly === undefined ? true : filters.activeOnly;
    const name = filters?.name?.trim() ? filters.name.trim() : null;
    const limit = pagination?.limit ?? 50;
    const offset = pagination?.offset ?? 0;
    return this.http
      .post<GraphqlHttpResponse<{ productAttributeDefinitions: { items: any[]; total: number; limit: number; offset: number } }>>(
        this.graphqlUrl(),
        {
          query: LIST_QUERY,
          variables: { activeOnly, name, limit, offset },
        }
      )
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => {
          const definitions = (data.productAttributeDefinitions?.items ?? []).map(
            mapProductAttributeDefinitionDto
          );
          return createPaginatedResult({
            items: definitions,
            total: data.productAttributeDefinitions?.total ?? 0,
            limit: data.productAttributeDefinitions?.limit ?? limit,
            offset: data.productAttributeDefinitions?.offset ?? offset,
          });
        })
      );
  }

  searchByName(name: string): Observable<PaginatedResult<ProductAttributeDefinition>> {
    const term = name.trim();
    if (!term) {
      return this.list();
    }
    return this.http
      .post<GraphqlHttpResponse<GraphqlProductAttributeDefinitionByIdDataDto>>(this.graphqlUrl(), {
        query: BY_NAME_QUERY,
        variables: { name: term },
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => {
          const items = asDefinitionDtoArray(data.productAttributeDefinition).map(mapProductAttributeDefinitionDto);
          return createPaginatedResult({
            items,
            total: items.length,
            limit: 50,
            offset: 0,
          });
        })
      );
  }

  getById(id: string): Observable<ProductAttributeDefinition | null> {
    return this.http
      .post<GraphqlHttpResponse<GraphqlProductAttributeDefinitionByIdDataDto>>(this.graphqlUrl(), {
        query: BY_ID_QUERY,
        variables: { id },
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => {
          const list = asDefinitionDtoArray(data.productAttributeDefinition);
          return list[0] ? mapProductAttributeDefinitionDto(list[0]) : null;
        })
      );
  }

  create(input: {
    name: string;
    slug?: string | null;
    position?: number | null;
    active?: boolean;
  }): Observable<AttributeDefinitionMutationResult> {
    const slugTrimmed = input.slug?.trim();
    const variables: Record<string, unknown> = {
      name: input.name.trim(),
      position: input.position ?? null,
      active: input.active ?? true,
    };
    if (slugTrimmed) {
      variables['slug'] = slugTrimmed;
    }
    return this.http
      .post<
        GraphqlHttpResponse<{
          createProductAttributeDefinition?: GraphqlMutationAttributeDefinitionPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: CREATE_MUTATION,
        variables,
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => mapMutationDefinitionPayload(data.createProductAttributeDefinition))
      );
  }

  update(input: {
    id: string;
    name?: string;
    slug?: string | null;
    position?: number | null;
    active?: boolean;
  }): Observable<AttributeDefinitionMutationResult> {
    const variables: Record<string, unknown> = { id: input.id };
    if (input.name !== undefined) {
      variables['name'] = input.name.trim();
    }
    if (input.slug !== undefined) {
      const s = input.slug?.trim();
      variables['slug'] = s === '' ? null : s;
    }
    if (input.position !== undefined) {
      variables['position'] = input.position;
    }
    if (input.active !== undefined) {
      variables['active'] = input.active;
    }
    return this.http
      .post<
        GraphqlHttpResponse<{
          updateProductAttributeDefinition?: GraphqlMutationAttributeDefinitionPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: UPDATE_MUTATION,
        variables,
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => mapMutationDefinitionPayload(data.updateProductAttributeDefinition))
      );
  }

  destroy(id: string): Observable<AttributeDefinitionMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          destroyProductAttributeDefinition?: GraphqlMutationAttributeDefinitionPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: DESTROY_MUTATION,
        variables: { id },
      })
      .pipe(
        map((res) =>
          mapEntityMutationResult(res, 'destroyProductAttributeDefinition', (payload) =>
            mapMutationDefinitionPayload(payload as GraphqlMutationAttributeDefinitionPayloadDto | null | undefined)
          )
        )
      );
  }

  updateActive(id: string, active: boolean): Observable<AttributeDefinitionMutationResult> {
    return this.update({ id, active });
  }

  permanentlyDelete(id: string): Observable<{ success: boolean; errors: string[] }> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          permanentlyDeleteProductAttributeDefinition?: { success?: boolean; errors?: string[] } | null;
        }>
      >(this.graphqlUrl(), {
        query: PERMANENTLY_DELETE_MUTATION,
        variables: { id },
      })
      .pipe(map((res) => mapLifecycleMutationResult(res, 'permanentlyDeleteProductAttributeDefinition')));
  }
}
